import React, { useState } from 'react';
import { Building2, ChevronRight, Ellipsis, Eye, PenLine, Trash2 } from 'lucide-react';
import { OrganizationHierarchy } from '../../types/organization';
import { useAuth } from '../../contexts/AuthContext';
import { canDeleteOrganization, canUpdateOrganization } from '../../utils/permissions';
import { useTaxonomyColors } from '../../hooks/useTaxonomyColors';
import {
  Avatar,
  Card,
  CategoryPill,
  EmptyState,
  IconButton,
  Menu,
  MenuItem,
  Swatch,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../ui';

interface HierarchicalOrganizationTableProps {
  organizations: OrganizationHierarchy[];
  onOrganizationSelect: (id: string) => void;
  onOrganizationEdit: (id: string) => void;
  onOrganizationDelete: (id: string) => void;
  onOrganizationView: (id: string) => void;
  deletingId?: string | null;
}

interface OrganizationRowProps {
  organization: OrganizationHierarchy;
  level: number;
  expandedNodes: Set<string>;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onView: (id: string) => void;
  deletingId?: string | null;
  /** Only when the API sends assignments; hierarchy nodes usually have none */
  showPeople?: boolean;
}

/** "religious-organization" → "Religious organization" */
const typeLabel = (type?: string) => {
  if (!type) return '';
  // Only reformat slugs; labels such as "Non-Governmental Organization (NGO)" stay as they are
  if (!/^[a-z0-9]+([-_][a-z0-9]+)*$/.test(type)) return type;
  const text = type.replace(/[-_]+/g, ' ');
  // Short single words are acronyms: "ngo" → "NGO"
  if (!text.includes(' ') && text.length <= 4) return text.toUpperCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** People count when the API includes assignments; hierarchy nodes usually do not. */
const peopleCount = (organization: OrganizationHierarchy): number | null => {
  const assignments = (organization as any).assignments;
  if (!Array.isArray(assignments)) return null;
  return new Set(assignments.map((a: any) => a.stakeholderId ?? a.stakeholder?.id ?? a.id)).size;
};

const rowMenuItems = (
  user: ReturnType<typeof useAuth>['user'],
  organization: OrganizationHierarchy,
  { onView, onEdit, onDelete, deletingId }: Pick<OrganizationRowProps, 'onView' | 'onEdit' | 'onDelete' | 'deletingId'>,
): MenuItem[] => {
  const items: MenuItem[] = [{ label: 'View', icon: Eye, onSelect: () => onView(organization.id) }];
  if (canUpdateOrganization(user, organization as any)) {
    items.push({ label: 'Edit', icon: PenLine, onSelect: () => onEdit(organization.id) });
  }
  if (canDeleteOrganization(user, organization as any)) {
    items.push({
      label: deletingId === organization.id ? 'Deleting' : 'Delete',
      icon: Trash2,
      danger: true,
      disabled: deletingId === organization.id,
      onSelect: () => onDelete(organization.id),
    });
  }
  return items;
};

/** Organizations read as teal rounded squares; a logo replaces the initials */
const OrgMark: React.FC<{ organization: OrganizationHierarchy }> = ({ organization }) => {
  const logoUrl = (organization as any).logoUrl as string | undefined;
  return (
    <Avatar
      kind="organization"
      size="xs"
      name={organization.name}
      image={logoUrl ? <img src={logoUrl} alt="" className="h-full w-full object-cover" /> : undefined}
    />
  );
};

/** One hairline per ancestor level, spanning the full row height */
const IndentGuides: React.FC<{ level: number; step: number }> = ({ level, step }) => (
  <>
    {Array.from({ length: level }).map((_, index) => (
      <span key={index} aria-hidden="true" className="relative flex-shrink-0 self-stretch" style={{ width: step }}>
        <span className="absolute inset-y-0 left-1/2 w-px bg-neutral-200" />
      </span>
    ))}
  </>
);

const OrganizationRow: React.FC<OrganizationRowProps> = ({
  organization,
  level,
  expandedNodes,
  onToggle,
  onSelect,
  onEdit,
  onDelete,
  onView,
  deletingId,
  showPeople,
}) => {
  const hasChildren = organization.children && organization.children.length > 0;
  const isExpanded = expandedNodes.has(organization.id);
  const { user } = useAuth();
  const { colorOf } = useTaxonomyColors();
  const people = peopleCount(organization);

  return (
    <>
      <TR interactive className="group" onClick={() => onSelect(organization.id)}>
        <TD className="py-0 pl-3">
          <div className="flex h-11 min-w-0 items-center">
            <IndentGuides level={level} step={20} />
            {hasChildren ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(organization.id);
                }}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? `Collapse ${organization.name}` : `Expand ${organization.name}`}
                className="mr-1 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-200/70 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
              >
                <ChevronRight
                  className={`h-3.5 w-3.5 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                  strokeWidth={2}
                />
              </button>
            ) : (
              <span className="mr-1 w-5 flex-shrink-0" />
            )}
            <OrgMark organization={organization} />
            <span className="ml-2.5 truncate text-ink">{organization.name}</span>
          </div>
        </TD>
        <TD>
          {organization.type ? (
            <CategoryPill
              title={organization.type}
              color={colorOf('organization_type', organization.type)}
              label={typeLabel(organization.type)}
            />
          ) : (
            <span className="text-neutral-300">—</span>
          )}
        </TD>
        <TD className="text-right tabular-nums text-neutral-500">
          {organization.children?.length ? organization.children.length : <span className="text-neutral-300">—</span>}
        </TD>
        {showPeople && (
          <TD className="hidden text-right tabular-nums text-neutral-500 lg:table-cell">
            {people ? people : <span className="text-neutral-300">—</span>}
          </TD>
        )}
        <TD className="pr-2 text-right">
          <div className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
            <Menu
              items={rowMenuItems(user, organization, { onView, onEdit, onDelete, deletingId })}
              trigger={(props) => (
                <IconButton label={`Actions for ${organization.name}`} size="sm" className="text-neutral-400" {...props}>
                  <Ellipsis className="h-4 w-4" strokeWidth={1.75} />
                </IconButton>
              )}
            />
          </div>
        </TD>
      </TR>
      {hasChildren &&
        isExpanded &&
        organization.children.map((child) => (
          <OrganizationRow
            key={child.id}
            organization={child}
            level={level + 1}
            expandedNodes={expandedNodes}
            onToggle={onToggle}
            onSelect={onSelect}
            onEdit={onEdit}
            onDelete={onDelete}
            onView={onView}
            deletingId={deletingId}
            showPeople={showPeople}
          />
        ))}
    </>
  );
};

const MobileOrganizationRow: React.FC<OrganizationRowProps> = ({
  organization,
  level,
  expandedNodes,
  onToggle,
  onSelect,
  onEdit,
  onDelete,
  onView,
  deletingId,
}) => {
  const hasChildren = organization.children && organization.children.length > 0;
  const isExpanded = expandedNodes.has(organization.id);
  const { user } = useAuth();
  const { colorOf } = useTaxonomyColors();
  const typeColor = colorOf('organization_type', organization.type);
  const childCount = organization.children?.length || 0;
  const menuItems = rowMenuItems(user, organization, { onView, onEdit, onDelete, deletingId });

  return (
    <>
      <li className="flex min-h-[56px] items-stretch pl-4 pr-1.5 transition-colors first:rounded-t-2xl last:rounded-b-2xl active:bg-neutral-100">
        <IndentGuides level={level} step={16} />
        <button
          type="button"
          onClick={() => onSelect(organization.id)}
          className="flex min-w-0 flex-1 items-center gap-3 py-2.5 text-left focus-visible:outline-none"
        >
          <OrgMark organization={organization} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] text-ink">{organization.name}</span>
            <span className="mt-0.5 block truncate text-[12px] text-neutral-400">
              {typeColor && <Swatch color={typeColor} className="mr-1.5 align-middle" />}
              {typeLabel(organization.type) || 'No type'}
              {childCount > 0 && ` · ${childCount} sub-org${childCount === 1 ? '' : 's'}`}
            </span>
          </span>
        </button>
        <div className="flex flex-shrink-0 items-center">
          <Menu
            items={menuItems}
            trigger={(props) => (
              <IconButton label={`Actions for ${organization.name}`} className="h-10 w-10 text-neutral-400" {...props}>
                <Ellipsis className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </IconButton>
            )}
          />
          {hasChildren ? (
            <IconButton
              label={isExpanded ? 'Collapse' : 'Expand'}
              aria-expanded={isExpanded}
              onClick={() => onToggle(organization.id)}
              className="h-10 w-10 text-neutral-400"
            >
              <ChevronRight
                className={`h-[18px] w-[18px] transition-transform duration-200 ${isExpanded ? 'rotate-90 text-ink' : ''}`}
                strokeWidth={1.75}
              />
            </IconButton>
          ) : (
            <span className="w-10" aria-hidden="true" />
          )}
        </div>
      </li>

      {hasChildren &&
        isExpanded &&
        organization.children.map((child) => (
          <MobileOrganizationRow
            key={child.id}
            organization={child}
            level={level + 1}
            expandedNodes={expandedNodes}
            onToggle={onToggle}
            onSelect={onSelect}
            onEdit={onEdit}
            onDelete={onDelete}
            onView={onView}
            deletingId={deletingId}
          />
        ))}
    </>
  );
};

export const HierarchicalOrganizationTable: React.FC<HierarchicalOrganizationTableProps> = ({
  organizations,
  onOrganizationSelect,
  onOrganizationEdit,
  onOrganizationDelete,
  onOrganizationView,
  deletingId,
}) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  if (organizations.length === 0) {
    return (
      <Card>
        <EmptyState icon={Building2} title="No organizations" />
      </Card>
    );
  }

  const hasAssignments = (nodes: OrganizationHierarchy[]): boolean =>
    nodes.some((node) => peopleCount(node) !== null || hasAssignments(node.children || []));

  const rowProps = {
    showPeople: hasAssignments(organizations),
    level: 0,
    expandedNodes,
    onToggle: toggleNode,
    onSelect: onOrganizationSelect,
    onEdit: onOrganizationEdit,
    onDelete: onOrganizationDelete,
    onView: onOrganizationView,
    deletingId,
  };

  return (
    <>
      {/* Desktop */}
      <Card className="hidden sm:block">
        <div className="overflow-x-auto">
          <Table className="table-fixed">
            <THead>
              <tr>
                <TH className="pl-4">Name</TH>
                <TH className="w-[28%]">Type</TH>
                <TH className="w-[88px] text-right">Sub-orgs</TH>
                {rowProps.showPeople && <TH className="hidden w-[88px] text-right lg:table-cell">People</TH>}
                <TH className="w-[56px]">
                  <span className="sr-only">Actions</span>
                </TH>
              </tr>
            </THead>
            <TBody>
              {organizations.map((org) => (
                <OrganizationRow key={org.id} organization={org} {...rowProps} />
              ))}
            </TBody>
          </Table>
        </div>
      </Card>

      {/* Phones: stacked tappable rows */}
      <Card className="sm:hidden">
        <ul className="divide-y divide-neutral-200/70">
          {organizations.map((org) => (
            <MobileOrganizationRow key={org.id} organization={org} {...rowProps} />
          ))}
        </ul>
      </Card>
    </>
  );
};
